// Bottom-right banner that rings when a `webrtc:invite` arrives. Accept
// navigates to the conversation and flips inVideo so the offer is answered.
// Reject emits the response and clears the banner.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AgentId, SessionId, VisitorId } from '@prakash-software/shared';
import { getBus } from '@prakash-software/shared';
import { useAgentStore } from '../stores/agent';
import { useConversationsStore } from '../stores/conversations';
import { useIncomingCallsStore, type IncomingCall } from '../stores/incoming-calls';
import { useOutgoingCallsStore } from '../stores/outgoing-calls';
import { clearBufferedOffer } from '../stores/webrtc-buffer';
import { claimOwnership } from '../lib/call-control';
import { playRingtone, stopRingtone } from '../lib/ringtone';
import { visitorDisplayName } from '../lib/visitor-name';
import styles from './IncomingCallBanner.module.css';

export function IncomingCallBanner() {
  const calls = useIncomingCallsStore((s) => s.calls);
  const loggedIn = useAgentStore((s) => s.current !== null);

  // Ring while at least one banner is up. Stop the moment they're all gone.
  useEffect(() => {
    if (loggedIn && calls.size > 0) {
      playRingtone('incoming');
      return () => stopRingtone('incoming');
    }
    return undefined;
  }, [loggedIn, calls.size]);

  if (!loggedIn) return null;
  if (calls.size === 0) return null;

  return (
    <div className={styles.deck} role="status" aria-live="polite">
      {[...calls.values()].map((c) => (
        <CallRow key={c.sessionId} call={c} />
      ))}
    </div>
  );
}

function CallRow({ call }: { call: IncomingCall }) {
  const navigate = useNavigate();
  const drop = useIncomingCallsStore((s) => s.drop);
  const me = useAgentStore((s) => s.current);

  function respond(response: 'accept' | 'reject') {
    if (!me) return;
    const bus = getBus();
    bus.emit({
      type: 'webrtc:invite:response',
      sessionId: call.sessionId,
      from: me.agentId,
      to: call.from as VisitorId | AgentId,
      response,
      at: Date.now(),
    });
    drop(call.sessionId);
    if (response === 'accept') {
      // Bulletproof state for the new call. The conversation might be:
      //   - absent (cross-agent fan-out reached us cold) → ensure stub
      //   - present but with stale ownership/video state from prior calls
      // We refresh BOTH inVideo and ownership, and proactively clear any
      // leftover caller/buffer state for this session so the answerer-path
      // mounts cleanly without stale offers from a previous call.
      const convStore = useConversationsStore.getState();
      const existing = convStore.byId.get(call.sessionId);
      if (!existing) {
        convStore.ensure({
          sessionId: call.sessionId,
          visitorId: call.from as VisitorId,
          visitorName: visitorDisplayName(call.from, call.fromName),
          medium: 'chat',
          startedBy: 'visitor',
          startedAt: call.at,
          messages: [],
          notes: '',
          notesSaveState: 'idle',
          notesUpdatedAt: null,
          summary: null,
          summaryProvider: null,
          summaryGeneratedAt: null,
          callTranscripts: new Map(),
          inVideo: true,
          ownedBy: 'agent',
          ownerAgentId: me.agentId,
        });
      } else {
        // Refresh both fields — `ensure` is no-op when present, and we want
        // the new call to claim ownership even if a prior call left ownedBy
        // as 'ai' or pointed at a different agent.
        convStore.setOwner(call.sessionId, 'agent', me.agentId);
        convStore.setVideo(call.sessionId as SessionId, true);
      }
      // Defensive: this call is incoming → we are the ANSWERER. Drop any
      // stale caller flag that might trick the Conversation route into
      // calling startAgentOffer instead of watchForOffer.
      const out = useOutgoingCallsStore.getState();
      out.drop(call.sessionId);
      out.clearCaller(call.sessionId);
      // Defensive: drop any leftover offer that was buffered from a prior
      // call on the same session id.
      clearBufferedOffer(call.sessionId);
      // Accepting a call IS the takeover. Flip ownership locally, broadcast
      // ai:escalate so the visitor's widget switches mode and the server
      // orchestrator stops generating AI replies.
      claimOwnership(call.sessionId, 'accepted-incoming-call');
      navigate(`/conversation/${call.sessionId}`);
    }
  }

  return (
    <article className={styles.banner}>
      <span className={styles.ringMark} aria-hidden="true">
        ▶
      </span>
      <div className={styles.body}>
        <p className={styles.kicker}>Incoming video call</p>
        <p className={styles.title}>{call.fromName} is calling.</p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.accept}
            onClick={() => respond('accept')}
          >
            Accept
          </button>
          <button
            type="button"
            className={styles.reject}
            onClick={() => respond('reject')}
          >
            Decline
          </button>
        </div>
      </div>
    </article>
  );
}
